import { useCallback } from 'react'
import { featureFlagService } from '@services/featureFlags'

import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { isReactNativeApp } from '@amzn/jwo-store-management-platform-components'

import { StoreIdServiceFor3P } from '@pages/StoreIdSelectionFor3P/utils/StoreIdServiceFor3P'
import { IhmIdentityBrokerTokenProvider } from '@api/utils/IhmIdentityBrokerTokenProvider'
import { IhmIdentityBrokerServiceClient } from '@api/IhmIdentityBrokerServiceClient'
import { LogoutUtils } from '@pages/LogoutPage/utils/LogoutUtils'
import { IIBServiceClient } from '@api/IIBServiceClient'
import { useAuth } from '@services/auth/AuthContext'

/**
 * Shared sign-out handler used by every "Sign Out" entry point
 * (profile bottom sheet, sidebar footer, cloudscape navbar profile).
 *
 * Branch selection mirrors the runtime auth contexts:
 *  - React Native: native-managed auth via the unified AuthContext logout.
 *  - IIB BYOD (isIhmIdentityBrokerAuthN): reset 3P store details, clear the
 *    identity broker token, and redirect to the identity broker login URI.
 *  - IIB (isIIBAuthN): the existing IIBServiceClient logout flow.
 *  - Desktop-web (Cognito/OAuth): the unified AuthContext logout, which clears
 *    the token provider + web session and flips isAuthenticated to false.
 *
 * On failure of the unified logout, the user is notified to retry and
 * isAuthenticated is left unchanged (AuthContext re-throws before updating it).
 */
export const useLogout = (): (() => Promise<void>) => {
    const { logout: authLogout } = useAuth()

    return useCallback(async () => {
        console.log('ATBase: Logging out...')

        if (isReactNativeApp()) {
            try {
                await authLogout()
                featureFlagService.clearCache()
            } catch (error) {
                console.error('SMP: Logout failed:', error)
                alert('Logout failed. Please try again.')
            }
            return
        }

        if (EnvironmentDetail.isIhmIdentityBrokerAuthN()) {
            console.log('ATBase: IIB BYOD flow detected, redirecting to IIB login page.')
            StoreIdServiceFor3P.resetStoreIdServiceFor3P()
            IhmIdentityBrokerTokenProvider.clear()
            if (EnvironmentDetail.isAndroid()) {
                console.debug('Clearing all the cookies from the Webview of the device')
                await LogoutUtils.clearAllCookiesFromWebView()
            }
            window.location.replace(IhmIdentityBrokerServiceClient.getIhmIdentityBrokerLoginUri())
            return
        }

        if (EnvironmentDetail.isIIBAuthN()) {
            IIBServiceClient.logout()
            featureFlagService.clearCache()
            return
        }

        // Desktop_Web_Context (Cognito/OAuth web login): unified logout path.
        try {
            await authLogout()
            featureFlagService.clearCache()
        } catch (error) {
            console.error('SMP: Logout failed:', error)
            alert('Logout failed. Please try again.')
        }
    }, [authLogout])
}
